import FormUtils from './utils/FormUtils';
import StringUtils from './utils/StringUtils';
import { resolveFinalizeBaselineAction } from './utils/formDirtyBaseline';
import { resolveFormEditingModeEnabled } from './utils/formEditingMode';

export default class Form {
	initialFormData = {};
	// scatena i change dei campi form al keyup in modo da non dover levare il focus
	dispatchChangeTimeout = null;

	_finalize_initial_state_timer = null;

	/** Until first finalizeInitialFormState(), defer dirty checks (Select2/Ace/widgets still hydrating). */
	_initial_baseline_pending = true;

	/** Timestamp after which widget-init changes stop triggering automatic re-baseline. */
	_init_settle_deadline = 0;

	/**
	 * Set the first time the user genuinely edits the form. Freezes the dirty baseline so an edit
	 * made right after opening (while widgets are still settling) is not folded into the baseline
	 * and lost. Programmatic widget-init changes never set this.
	 */
	_user_interacted = false;

	/**
	 * Autosave restore injects draft values into the DOM before the dirty baseline is
	 * captured, so isDirty() stays false. This flag keeps Save / leave-confirm active
	 * until the user persists (or discards) that restored draft.
	 */
	_unsaved_autosave_restore = false;

	/** Guardia anti-rientro: evita una seconda discard se #close viene ricliccato durante l'attesa. */
	_closing_after_discard = false;

	needsConfirm = false;

	/** When true, formChangeHandler must not re-attach beforeunload (e.g. during async save). */
	suppressLeaveConfirm = false;

	/** Prevents re-entrant leave-guard refresh during formValidation callbacks. */
	_refresh_save_button_state_running = false;

	_boundBeforeUnload = null;

	constructor() {
		this._init_settle_deadline = Date.now() + 3000;
		this._boundBeforeUnload = this.askConfirmPageLeave.bind(this);
		this.initializeEvents();
		this.initializeFormConfigurator();
		this.initializeSeoChecker();
		this.initializeSummernote();

		// Establish the dirty-tracking baseline for every form. Pages with async widgets (Select2,
		// FormTabs, content editors) re-schedule this after hydration, but forms without any of them
		// (e.g. settings/languages) would otherwise never clear _initial_baseline_pending — leaving
		// isDirty()/shouldAttemptSave() permanently false so Save never POSTs.
		this.scheduleFinalizeInitialFormState();
	}

	// setConfirmPageLeave() {
	// 	this.needsConfirm = true;
	// 	window.needsConfirm = true;
	// 	this._boundAskConfirmPageLeave = this._boundAskConfirmPageLeave ?? this.askConfirmPageLeave.bind(this);
	// 	window.addEventListener('beforeunload', this._boundAskConfirmPageLeave);
	// }

	// askConfirmPageLeave(e) {
	// 	const confirmationMessage = 'Sei sicuro di voler uscire? Le modifiche non salvate andranno perse.';
	// 	e.returnValue = confirmationMessage;
	// 	return confirmationMessage;
	// }

	// clearConfirmPageLeave() {
	// 	this.needsConfirm = false;
	// 	window.needsConfirm = false;
	// 	const handler = this._boundAskConfirmPageLeave ?? this.askConfirmPageLeave;
	// 	window.removeEventListener('beforeunload', handler);
	// }

	setConfirmPageLeave() {
		if (this.suppressLeaveConfirm) {
			return;
		}

		this.needsConfirm = true;
		window.removeEventListener('beforeunload', this._boundBeforeUnload);
		window.addEventListener('beforeunload', this._boundBeforeUnload);
	}

	askConfirmPageLeave(e) {
		const confirmationMessage = 'Sei sicuro di voler uscire? Le modifiche non salvate andranno perse.';
		e.returnValue = confirmationMessage;
		return confirmationMessage;
	}

	clearConfirmPageLeave() {
		this.needsConfirm = false;
		window.removeEventListener('beforeunload', this._boundBeforeUnload);
	}

	/** Visible fields only — used for keyup → change delegation. */
	static getTrackedFieldSelector() {
		return ':input[id]:not(button):visible, :input[id][data-plugin="switchery"]:not(button), :input[id][data-ace-editor-sync]:not(button), .switchery:not(#form-editing-mode):visible, #crop_updated, #editor_updated, #crop_caption';
	}

	/** All enabled fields including hidden tabs — used for dirty baseline/snapshot. */
	static getBaselineFieldSelector() {
		// ACL tree checkboxes are tracked via #acl_selection_state (one fingerprint), not per-node:
		// large role trees would make every click scan hundreds/thousands of inputs.
		return '.field-container:not(.user-disabled) :input[id]:not(button):not(:disabled):not(.acl-tree__cb), .field-container:not(.user-disabled) textarea[id][data-ace-editor-sync], input[id][data-plugin="switchery"]:not(:disabled), #crop_updated, #editor_updated, #crop_caption';
	}

	/**
	 * Required/HTML5 checks must ignore fields in hidden tabs or disabled containers (same scope as FormValidation excluded fields).
	 *
	 * @param {Element} element
	 * @returns {boolean}
	 */
	static isFieldVisibleForValidation(element) {
		const $field = $(element);
		const $container = $field.closest('.field-container');

		if ($container.length && ($container.hasClass('hidden') || $container.hasClass('user-disabled'))) {
			return false;
		}

		if ($field.is('[data-ace-editor-sync]') || $field.is('[data-plugin="switchery"]')) {
			return true;
		}

		return $field.is(':visible');
	}

	/**
	 * New categorization/content translation opened in the side iframe (draft row, no persisted id yet).
	 *
	 * @returns {boolean}
	 */
	isNewTranslationDraft() {
		if (!document.body.classList.contains('popup-translation')) {
			return false;
		}

		const raw_id = $('#pageForm input[name="id"]').val() ?? $('#id').val();

		if (raw_id === undefined || raw_id === null) {
			return true;
		}

		const normalized_id = String(raw_id).trim();

		return normalized_id.length === 0 || normalized_id === '0';
	}

	/**
	 * A record that does not exist yet on the server. When its fields were preloaded server-side
	 * (?fastops=agency|archive|whatsapp|videos|events|import_stories, duplicate, …) the dirty
	 * baseline matches the form as rendered, so Save stays available (see isPreloadedNewRecord).
	 *
	 * Content pages (stories/events/multimedia) publish window.isNewContent and keep it in sync
	 * after an async save-stay, so it is the authoritative signal there. Other forms are judged
	 * on their id field; a form without one is never assumed to be new.
	 *
	 * @returns {boolean}
	 */
	isNewRecord() {
		if (window.isNewContent === true) {
			return true;
		}

		if (window.isNewContent === false) {
			return false;
		}

		const $form_id_field = $('#pageForm input[name="id"]').first();
		const $id_field = $form_id_field.length ? $form_id_field : $('#id').first();

		if (!$id_field.length) {
			return false;
		}

		const normalized_id = String($id_field.val() ?? '').trim();

		return normalized_id.length === 0 || normalized_id === '0';
	}

	/**
	 * Mark the form dirty after an autosave restore (?fastops=autosave). Restored values
	 * are already in the DOM when the baseline is captured, so field diffs alone never
	 * unlock Save — this explicit flag does.
	 */
	markDirtyFromAutosaveRestore() {
		this._unsaved_autosave_restore = true;
		this._user_interacted = true;
		this._initial_baseline_pending = false;
		this.setConfirmPageLeave();
		this.setSaveButtonsEnabled();
	}

	readTrackedFieldValue(input) {
		const $input = $(input);

		if (['checkbox', 'radio'].includes($input.attr('type'))) {
			const checked = $(`input:checked[name="${$input.attr('name')}"]`)
				.map((_i, el) => el.value)
				.toArray();

			return JSON.stringify(checked);
		}

		if ($input.hasClass('switchery')) {
			return $input.css('background-color') !== 'rgb(255, 255, 255)';
		}

		let value = $input.val();

		if (Array.isArray(value)) {
			value = JSON.stringify(value);
		}

		// Crop fields embed width-dependent display hints (canvasRatio/canvasLeftRatio/canvasTopRatio)
		// that change from pure layout reflow — e.g. the translation split panel opening/closing resizes
		// the croppers. Those aren't meaningful cover edits, so strip them from the dirty comparison; the
		// real crop geometry (x/y/width/height/rotate/scale) is still compared. Only the comparison value
		// is normalized — the stored field keeps the hints, so zoom/pan still restore correctly.
		const id = $input.attr('id');

		if (typeof value === 'string' && id && id.startsWith('crop_') && value.includes('cropper')) {
			value = Form.normalizeCropFieldValueForDiff(value);
		}

		return value;
	}

	/**
	 * Drop width-dependent display hints from a crop field's JSON so they don't trigger false dirtiness.
	 *
	 * @param {string} raw
	 * @returns {string}
	 */
	static normalizeCropFieldValueForDiff(raw) {
		try {
			const parsed = JSON.parse(raw);
			const cropper = parsed?.cropper;

			if (cropper && typeof cropper === 'object') {
				delete cropper.canvasRatio;
				delete cropper.canvasLeftRatio;
				delete cropper.canvasTopRatio;
			}

			return JSON.stringify(parsed);
		} catch {
			return raw;
		}
	}

	collectTrackedFieldSnapshot() {
		const snapshot = {};

		$('#pageForm')
			.find(Form.getBaselineFieldSelector())
			.each((_idx, element) => {
				const id = $(element).attr('id');

				if (!id) {
					return;
				}

				snapshot[id] = this.readTrackedFieldValue(element);
			});

		return snapshot;
	}

	captureInitialFormData() {
		this.initialFormData = this.collectTrackedFieldSnapshot();
	}

	/**
	 * Fold a subset of fields into the dirty baseline so they stop counting as unsaved changes,
	 * without disturbing the rest of the form. Used by the translation split view: the cover/crop is
	 * shared family-wide and synced live between the two panels, so a change MIRRORED in from the other
	 * panel is external sync — not a local unsaved edit — and must not leave this form's Save buttons
	 * lit. Genuine local edits to other fields (title, …) keep the form dirty.
	 *
	 * @param {string[]} ids Field ids to re-baseline (e.g. cover/crop hidden inputs).
	 */
	rebaselineFields(ids) {
		if (!this.initialFormData || !Array.isArray(ids)) {
			return;
		}

		ids.forEach((id) => {
			const element = document.getElementById(id);

			if (element && Object.prototype.hasOwnProperty.call(this.initialFormData, id)) {
				this.initialFormData[id] = this.readTrackedFieldValue(element);
			}
		});

		if (!this.isDirty()) {
			this.needsConfirm = false;
			this.clearConfirmPageLeave();
		}

		this.setSaveButtonsEnabled();
	}

	/**
	 * Re-baseline after deferred widgets (Select2 AJAX, FormTabs, validation). Debounce to absorb late DOM updates.
	 *
	 * @param {number} [delay_ms]
	 */
	scheduleFinalizeInitialFormState(delay_ms = 450) {
		if (this._finalize_initial_state_timer) {
			clearTimeout(this._finalize_initial_state_timer);
		}

		this._finalize_initial_state_timer = setTimeout(() => {
			this._finalize_initial_state_timer = null;
			this.finalizeInitialFormState();
		}, delay_ms);
	}

	/**
	 * Re-baseline after deferred widgets (Ace, Select2, crops). Clears leave guard when values match snapshot.
	 */
	finalizeInitialFormState() {
		const action = resolveFinalizeBaselineAction({
			userInteracted: this._user_interacted,
		});

		// Once the user has edited, never move the baseline again: a late widget re-baseline would
		// fold their changes into it and they'd stop being detected as unsaved (REDMINE-2209:
		// changing the content model during hydration used to be absorbed here, leaving Save off
		// while leave-confirm still saw later async field rewrites as dirty).
		if (action === 'keep-user-edits') {
			if (this._initial_baseline_pending) {
				this._initial_baseline_pending = false;
				// Keep whatever snapshot we already have (often still {}). Do NOT capture the
				// post-edit DOM as "clean" — that would hide the model/select change the user just made.
				this.setConfirmPageLeave();
				this.setSaveButtonsEnabled();
			}

			return;
		}

		if (this._initial_baseline_pending) {
			this._initial_baseline_pending = false;
		}

		this.captureInitialFormData();
		this.needsConfirm = false;
		this.clearConfirmPageLeave();
		this.setSaveButtonsEnabled(true);
	}

	/**
	 * Call after a successful save (especially save-stay / async) to reset dirty state and disable Save.
	 *
	 * @param {{ resetCropFlags?: boolean }} [options]
	 */
	markFormAsSaved(options = {}) {
		const reset_crop_flags = options.resetCropFlags === true;

		if (reset_crop_flags) {
			// Empty string, not "false": PHP empty("false") is false and would treat the next
			// save as a cover update without crop fields, wiping the cover (REDMINE-1822).
			$('#crop_updated').val('');
			$('#editor_updated').val('');
		}

		$('textarea[data-plugin="summernote"]').each((_idx, elem) => {
			const $textarea = $(elem);

			if ($textarea.next('.note-editor').length > 0) {
				$textarea.val($textarea.summernote('code'));
			}
		});

		this.captureInitialFormData();
		this._initial_baseline_pending = false;
		this._unsaved_autosave_restore = false;
		this.needsConfirm = false;
		this.clearConfirmPageLeave();
		// Form is clean again after a successful save: disable the save buttons until the
		// user makes a new edit. The translation proxy toolbar mirrors this via the bridge.
		this.setSaveButtonsEnabled();
	}

	/**
	 * Save buttons are enabled only when there are real changes to persist (or a brand-new
	 * translation draft). Readonly mode or a missing form always disables them. The `_enabled`
	 * argument is ignored — the state is recomputed from the form on every call so existing
	 * call sites simply mean "re-evaluate the save buttons".
	 *
	 * @param {boolean} [_enabled] Ignored — kept for call-site compatibility.
	 */
	setSaveButtonsEnabled(_enabled) {
		const $page_form = $('#pageForm');
		const $buttons = $('#save, #save-stay, #popupsave, #popupsave-stay');
		const form_available = $page_form.length > 0 && !window.forceReadonly;
		const enabled = form_available && this.shouldAttemptSave() && this.hasTitleForSave();

		$buttons.prop('disabled', !enabled);
		$buttons.toggleClass('disabled', !enabled);

		window.naxosTranslationToolbarBridge?.schedulePublishState?.();
	}

	/**
	 * Content pages (stories, events, multimedia) keep Save disabled while the title is empty
	 * (REDMINE-2158). Other required fields are still checked on click (red border).
	 *
	 * @returns {boolean}
	 */
	hasTitleForSave() {
		if (window.requireTitleForSave !== true) {
			return true;
		}

		const $title = $('#pageForm #title');

		return !$title.length || String($title.val() ?? '').trim() !== '';
	}

	resetSaveButtonLoadingState() {
		$('#save, #save-stay, #popupsave, #popupsave-stay').removeClass('submitting').css({ cursor: '', opacity: '' });
		window.naxosTranslationToolbarBridge?.schedulePublishState?.(true);
	}

	/**
	 * Sync forms (sections, folders, tags, …): if requestSubmit() never starts navigation, clear the
	 * stuck spinner and surface an error instead of leaving the translation proxy toolbar spinning.
	 *
	 * @param {JQuery} $btn
	 * @param {number} [delay_ms]
	 */
	scheduleSyncSubmitSafetyReset($btn, delay_ms = 3000) {
		const clear_unload_listeners = () => {
			window.removeEventListener('beforeunload', on_page_unload);
			window.removeEventListener('pagehide', on_page_unload);
		};

		const on_page_unload = () => {
			clearTimeout(safety_timer);
			clear_unload_listeners();
		};

		const safety_timer = setTimeout(() => {
			clear_unload_listeners();

			if (!$btn.hasClass('submitting')) {
				return;
			}

			this.resetSaveButtonLoadingState();
			window.toastr?.error?.(
				'Il salvataggio non è partito. Riprova o usa il pulsante Salva nella finestra di traduzione.',
			);
		}, delay_ms);

		window.addEventListener('beforeunload', on_page_unload, { once: true });
		window.addEventListener('pagehide', on_page_unload, { once: true });
	}

	/**
	 * Runs HTML5 and FormValidation checks before persisting. Call on Save click/submit.
	 *
	 * @returns {Promise<boolean>}
	 */
	async validateForSubmit() {
		const $page_form = $('#pageForm');

		if (!$page_form.length || window.forceReadonly) {
			return false;
		}

		this.revealRequiredFieldErrors();

		if (!this.validateFormFields()) {
			return false;
		}

		const validator = $page_form.data('formValidation');

		if (!validator) {
			return true;
		}

		await $page_form.formValidation('validate');
		const fv_valid = validator.isValid();

		return fv_valid === true;
	}

	/**
	 * True when the user has unsaved edits that should block navigation / translation panel.
	 */
	hasUnsavedUserChanges() {
		if (!$('#pageForm').length || window.forceReadonly || this._initial_baseline_pending) {
			return false;
		}

		return this._unsaved_autosave_restore || this.isDirty();
	}

	/**
	 * Whether a save action should persist (unsaved new record, first translation draft or dirty form).
	 */
	shouldAttemptSave() {
		if (!$('#pageForm').length || window.forceReadonly || this._initial_baseline_pending) {
			return false;
		}

		if (this.isNewTranslationDraft() || this._unsaved_autosave_restore) {
			return true;
		}

		// Preloaded new records can be saved as they are; a blank one ("+" button) needs an edit (REDMINE-2158).
		if (this.isNewRecord() && this.isPreloadedNewRecord()) {
			return true;
		}

		return this.isDirty();
	}

	/**
	 * New record opened with server-side preloaded values (?fastops=duplicate|agency|archive|…).
	 *
	 * @returns {boolean}
	 */
	isPreloadedNewRecord() {
		return new URLSearchParams(window.location.search).has('fastops');
	}

	/**
	 * Informs the user that Save was clicked but there is nothing new to persist.
	 */
	notifyNoChangesToSave() {
		window.toastr?.info?.('Non ci sono modifiche da salvare.');
	}

	/**
	 * Chiudere senza salvare equivale a buttare via la bozza: gli autosave della riga corrente
	 * (uuid, quindi solo questa lingua) vengono scartati prima di lasciare la pagina, così alla
	 * riapertura non viene riproposto un ripristino che l'utente ha appena rifiutato.
	 * Il fallback a tempo evita che una discard lenta o fallita blocchi la chiusura.
	 */
	discardAutosavesThen(done) {
		if (window.forceReadonly || typeof window.discardContentAutosaves !== 'function') {
			done();

			return;
		}

		let finished = false;
		const run = () => {
			if (finished) return;
			finished = true;
			done();
		};

		setTimeout(run, 1500);
		window.discardContentAutosaves().finally(run);
	}

	/**
	 * Shows custom leave-confirm modal (Annulla / Esci senza salvare / Salva ed esci).
	 * Used when user clicks #close with unsaved changes; browser beforeunload cannot be customized.
	 */
	showLeaveConfirmModal(closeHrefOrPopup) {
		const self = this;
		bootbox.dialog({
			title: 'Modifiche non salvate',
			message: 'Vuoi uscire dalla pagina? Le modifiche non salvate andranno perse. Puoi salvare prima di uscire.',
			closeButton: true,
			buttons: {
				cancel: {
					label: 'Annulla',
					className: 'btn-default',
					callback() {
						// Keep user on page; do nothing
					},
				},
				leave: {
					label: 'Esci senza salvare',
					className: 'btn-danger',
					callback() {
						self.clearConfirmPageLeave();
						if (typeof closeHrefOrPopup === 'string' && closeHrefOrPopup) {
							self.discardAutosavesThen(() => {
								window.location.href = closeHrefOrPopup;
							});
							return;
						}
						const is_popup_translation = document.body.classList.contains('popup-translation');
						const notifyParentClose = () => {
							window.parent.postMessage('allowClose', '*');
							if (is_popup_translation) {
								window.parent.postMessage({ action: 'close-translation-panel' }, '*');
							}
						};
						const discardThenClose = () => {
							self.discardAutosavesThen(notifyParentClose);
						};
						// Popup: unlock and notify parent (translation panel keeps family lock on parent).
						const $unlock = $('#unlockurl');
						if ($unlock.length && !is_popup_translation) {
							if (window.Utils?.LoaderShow) window.Utils.LoaderShow();
							if (window.pollingInterval) {
								clearInterval(window.pollingInterval);
								window.pollingInterval = null;
							}
							if (window.pollingCall) {
								window.pollingCall.abort("Annullato dall'utente");
								window.pollingCall = null;
							}
							$.ajax(StringUtils.localizeUrl($unlock.val())).then(() => discardThenClose());
							return;
						}
						if (is_popup_translation && window.Utils?.LoaderShow) {
							window.Utils.LoaderShow();
						}
						discardThenClose();
					},
				},
				saveAndLeave: {
					label: 'Salva ed esci',
					className: 'btn-primary',
					callback() {
						self.clearConfirmPageLeave();
						const $save = $('#save');
						const $popupSave = $('#popupsave');
						const $btn = $save.length ? $save : $popupSave;
						if ($btn.length) {
							if (typeof closeHrefOrPopup === 'string' && closeHrefOrPopup) {
								const closeUri = new URL(closeHrefOrPopup, window.location.origin);
								const closeBackUrl = closeUri.searchParams.get('back');
								if (closeBackUrl) {
									const originalFormAction = $('#pageForm').attr('action');
									const formUri = new URL(originalFormAction, window.location.origin);
									if (closeBackUrl !== originalFormAction) {
										window.originalFormAction = originalFormAction;
										formUri.searchParams.set('back', closeBackUrl);
										$('#pageForm').attr('action', formUri.toString());
									}
								}
							}
							// Native click() on a disabled button is a no-op (REDMINE-2209): re-enable
							// and use jQuery trigger so the async content save handler still runs.
							$btn.prop('disabled', false).removeClass('disabled');
							$btn.trigger('click');
						}
					},
				},
			},
		});
	}

	/**
	 * Returns true if the link would navigate away from the current page (not same-page anchor, not new tab).
	 */
	isNavigationLink(link) {
		const href = link.getAttribute('href');
		if (!href || href === '#' || href.startsWith('#')) return false;
		if (link.target === '_blank' || link.target === '_new') return false;
		if (link.hasAttribute('data-no-confirm-leave')) return false;
		// Lightbox anchors (editor image/gallery, previews): not real navigation — do not show leave modal
		const lightbox_link_classes = ['popup-preview-image', 'popup-preview-gallery-image', 'popup-preview-video', 'popup-preview-iframe'];
		if (lightbox_link_classes.some((cls) => link.classList.contains(cls))) return false;
		return true;
	}

	/**
	 * Resolves link href to absolute URL for later navigation.
	 */
	getLinkDestination(link) {
		return link.href;
	}

	/**
	 * Handles #close click: if there are unsaved changes, show custom modal instead of navigating.
	 */
	handleCloseClick(e) {
		const $close = $('#close');
		const href = $close.attr('href');
		const isPopup = $close.data('is-in-popup') === true || $close.data('is-in-popup') === 'true';

		// Base the guard on the real form state, not just the needsConfirm flag: after an async
		// save (save-stay) the form is clean again, so closing must proceed immediately even if a
		// deferred change event left needsConfirm stale-true (otherwise stopPropagation below would
		// block #close's inline unlockAndPost and the popup would not close on the first click).
		if (!this.hasUnsavedUserChanges()) {
			// Niente da confermare, ma la chiusura resta un "esci senza salvare": la bozza va
			// scartata anche qui (es. ripristino proposto e ignorato). Nei popup ci pensa
			// unlockAndPost, che va lasciato passare senza intercettare il click.
			if (!isPopup && href && !this._closing_after_discard && typeof window.discardContentAutosaves === 'function') {
				e.preventDefault();
				e.stopPropagation();
				this._closing_after_discard = true;
				this.discardAutosavesThen(() => {
					window.location.href = href;
				});
			}

			return;
		}
		e.preventDefault();
		e.stopPropagation();
		this.showLeaveConfirmModal(isPopup || !href ? null : href);
	}

	/**
	 * Handles clicks on any link that would navigate away: intercept when needsConfirm, show modal, store destination URL.
	 */
	handleNavigationClick(e) {
		if (!this.hasUnsavedUserChanges()) return;
		const link = e.target.closest('a');
		if (!link) return;
		// #close has its own handler (knows popup vs href); skip here to avoid double handling
		if (link.id === 'close') return;
		if (!this.isNavigationLink(link)) return;
		e.preventDefault();
		e.stopPropagation();
		const destination = this.getLinkDestination(link);
		this.showLeaveConfirmModal(destination);
	}

	initializeSummernote() {
		const $summernoteFields = $('textarea[data-plugin="summernote"]');
		if ($summernoteFields.length === 0) return;
		const originalIs = $.fn.is;
		$.fn.is = function (selector) {
			if (selector === ':visible' || selector === ':hidden' || selector === ':not(:visible)') {
				const $this = this;
				if ($this.length > 0 && $this.is('textarea[data-plugin="summernote"]')) {
					// Per i campi Summernote, controlla se l'editor è visibile
					const $editor = $this.next('.note-editor');
					if ($editor.length > 0) {
						const isEditorVisible = $editor.is(':visible');
						if (selector === ':visible') {
							return isEditorVisible;
						} else if (selector === ':hidden') {
							return !isEditorVisible;
						} else if (selector === ':not(:visible)') {
							return !isEditorVisible;
						}
					}
				}
			}
			// Per tutti gli altri casi, usa il metodo originale
			return originalIs.call(this, selector);
		};
		// inizializzazione campi editor summernote
		$summernoteFields.each((_idx, elem) => {
			const $element = $(elem);
			elem.checkValidity = () => {
				const excludeds = $('#pageForm')?.data('formValidation')?.options?.excluded || [];
				for (const excluded of excludeds) {
					if ($element.is(excluded)) return true;
				}
				return !$element.summernote('isEmpty');
			};
			const maxLength = $element.attr('data-seo-max');
			const summernoteOptions =
				window.summernoteOptions !== undefined
					? window.customSummernoteOptions.buttons !== undefined || window.customSummernoteOptions.toolbar !== undefined
						? window.customSummernoteOptions
						: window.customSummernoteOptions[$element.attr('id')]
					: {
							height: 150,
							toolbar: [
								['style', ['bold', 'italic', 'underline']],
								['para', ['ul', 'paragraph']],
								['insert', ['link', 'hr']],
							],
							popover: {
								image: [['remove', ['removeMedia']]],
							},
						};
			const options = Object.assign(summernoteOptions, {
				lang: 'it-IT',
				callbacks: {
					onDialogShown: () => {
						// Check if the dialog is the link dialog
						const linkDialog = $('.modal.note-modal.link-dialog.show');

						// If the link dialog is found
						if (linkDialog.length) {
							// Locate the checkbox div and uncheck/hide the checkbox
							const protocolCheckboxDiv = linkDialog.find('.checkbox.sn-checkbox-use-protocol');
							const protocolCheckbox = protocolCheckboxDiv.find('input[type="checkbox"]');

							// Uncheck the checkbox and hide the whole div
							protocolCheckbox.prop('checked', false).removeAttr('checked');
							protocolCheckbox.attr('aria-checked', 'false'); // Update the aria-checked attribute as well
							protocolCheckboxDiv.hide();

							// Clear the interval once we've made the modification
						}
					},
				},
			});

			if (maxLength) {
				options.callbacks.onKeydown = (e) => {
					var t = e.currentTarget.innerText;
					if (t.length >= maxLength && e.keyCode != 8) e.preventDefault();
				};
				options.callbacks.onKeyup = (e) => {
					var t = e.currentTarget.innerText;
					if (typeof callbackMax == 'function') {
						callbackMax(maxLength - t.length);
					}
				};
				options.callbacks.onPaste = (e) => {
					var t = e.currentTarget.innerText;
					var bufferText = ((e.originalEvent || e).clipboardData || window.clipboardData).getData('Text');
					e.preventDefault();
					var all = t + bufferText;
					document.execCommand('insertText', false, all.trim().substring(0, maxLength));
					if (typeof callbackMax == 'function') {
						callbackMax(maxLength - t.length);
					}
				};
			}

			$element
				.summernote(options)
				.on('summernote.paste', (_e, ne) => {
					var bufferText = ((ne.originalEvent || ne).clipboardData || window.clipboardData).getData('Text');
					ne.preventDefault();
					document.execCommand('insertText', false, bufferText);
				})
				.on('summernote.change', (e) => {
					// SINCRONIZZA il contenuto di Summernote nel textarea nascosto
					const $textarea = $(e.target);
					const content = $textarea.summernote('code');
					// Mantieni l'HTML nel textarea, non rimuoverlo
					$textarea.val(content);
					$textarea.trigger('change');
					const $pageForm = $('#pageForm');
					if ($pageForm) $pageForm.formValidation('revalidateField', $textarea.attr('name') || $textarea.attr('id'));
					// Forza il flag needsConfirm quando Summernote cambia
					this.formChangeHandler(e, true);
				});

			// Read-only fields (e.g. content locked by another user) carry data-readonly/readonly on
			// the textarea, but Summernote builds its own editable surface that ignores those HTML
			// attributes. Disable the editor explicitly so it can't be edited.
			if (window.forceReadonly || $element.attr('data-readonly') === 'true' || $element.is('[readonly]')) {
				$element.summernote('disable');
			}

			// "Ritraduci dall'originale": the button is rendered hidden next to the textarea (Blade only
			// emits it when editing a translation), so move it into this field's Summernote toolbar as the
			// last button now that the toolbar exists. translations.js handles the click via delegation.
			const fieldId = $element.attr('id');
			const $retranslateBtn = $element.parent().find(`.js-retranslate-summernote-btn[data-field-identifier="${fieldId}"]`);
			const $summernoteToolbar = $element.parent().find('.note-toolbar').first();
			if ($retranslateBtn.length && $summernoteToolbar.length) {
				$summernoteToolbar.append($('<div class="note-btn-group btn-group"></div>').append($retranslateBtn.removeClass('d-none')));
			}
		});
	}

	initializeFormConfigurator() {
		// CONFIGURATORE CAMPI FORMS
		if ($('.field-configurator').length) {
			$('.field-configurator').on('click', (e) => {
				const fieldName = e.currentTarget.getAttribute('data-field-identifier');
				const fieldConfigs = FormConfigs.find((field) => field.identifier === fieldName);
				if (!fieldConfigs) return;

				FormUtils.openFormFieldConfigurator(fieldConfigs);
			});
		}
	}

	initializeSeoChecker() {
		// gestione campi con proprietà max/min
		const seocheker = {
			init: () => {
				$('input[data-seo-max]').each((_index, e) => {
					seocheker.enforceMaxLength(e);
					seocheker.trig(e);
					// `input` covers typing, paste, cut, and programmatic `.trigger('input')`.
					// Native maxlength is bypassed by jQuery `.val()`, so we also enforce here.
					$(e).on('input', (ev) => {
						seocheker.enforceMaxLength(ev.currentTarget);
						seocheker.trig(ev.currentTarget);
					});
				});
			},

			enforceMaxLength: (e) => {
				const maxAttr = e.getAttribute('maxlength');

				if (maxAttr === null || maxAttr === '') {
					return;
				}

				const maxLength = parseInt(maxAttr, 10);

				if (Number.isNaN(maxLength) || e.value.length <= maxLength) {
					return;
				}

				e.value = StringUtils.truncateToLength(e.value, maxLength);
			},

			trig: (e) => {
				const fieldContainer = $(e).closest('.panel-body');
				let seocurrent = fieldContainer.find('.seo-current');
				let seodelta = fieldContainer.find('.seo-delta');

				let current = e.value.length;

				seocurrent.text(current);

				if (current < $(e).data('seo-min')) {
					seodelta.text(current - $(e).data('seo-min'));
					seodelta.addClass('text-danger');
				} else if (current > $(e).data('seo-max')) {
					seodelta.text('+' + (current - $(e).data('seo-max')));
					seodelta.addClass('text-danger');
				} else {
					seodelta.removeClass('text-danger').addClass('text-success');
					seodelta.text('OK');
				}
			},
		};
		seocheker.init();
	}

	initializeEvents() {
		const pageForm = $('#pageForm');
		// Intercept #close in capture phase so custom leave modal runs before inline onclick
		const closeEl = document.getElementById('close');
		if (closeEl) {
			closeEl.addEventListener('click', (e) => this.handleCloseClick(e), true);
		}
		// Intercept any navigation link click to show leave-confirm modal and store destination URL
		document.addEventListener('click', (e) => this.handleNavigationClick(e), true);

		pageForm.find(':input[id]:not(button):visible').on('keyup', (e) => {
			this._user_interacted = true;

			if (this.dispatchChangeTimeout) {
				clearTimeout(this.dispatchChangeTimeout);
				this.dispatchChangeTimeout = null;
			}

			this.dispatchChangeTimeout = setTimeout(() => {
				$(e.currentTarget).trigger('change');
				this.dispatchChangeTimeout = null;
			}, 500);
		});

		// Save buttons stay enabled; validation runs on submit instead of toggling disabled state.
		this.setSaveButtonsEnabled(true);

		pageForm.on('change', (e) => this.formChangeHandler(e));
		$(document).on('click', '.switchery:not(#form-editing-mode)', (e) => {
			this._user_interacted = true;
			setTimeout(() => this.formChangeHandler(e), 0);
		});

		// Select2 fires a programmatic (untrusted) `change`, so a first selection made during the
		// initial settle window would be absorbed into the dirty baseline instead of enabling Save.
		// `select2:select`/`select2:unselect` fire before that `change`, so flag the genuine user
		// interaction here to let the subsequent change handler mark the form dirty immediately.
		pageForm.on('select2:select select2:unselect', () => {
			this._user_interacted = true;
		});

		// Content model select (#model) is shared by stories, events, multimedia, sections, folders,
		// event categories/types and newsletter. Changing it can rewrite visible fields / cover widgets
		// asynchronously; re-check Save after those side effects settle (REDMINE-2209).
		pageForm.on('change', '#model', () => {
			this._user_interacted = true;
			window.setTimeout(() => this.refreshSaveButtonState(), 0);
			window.setTimeout(() => this.refreshSaveButtonState(), 400);
		});

		pageForm.on('submit.readonlyFields', () => {
			FormUtils.prepareReadonlyFieldsForSubmit(pageForm);
		});

		const syncCloseAfterSaveHiddenField = ($btn) => {
			if (!document.body.classList.contains('popup-translation') && !document.body.classList.contains('popup')) {
				return;
			}

			let $hidden = pageForm.find('input[name="_close_after_save"]');

			if (!$hidden.length) {
				$hidden = $('<input type="hidden" name="_close_after_save">').appendTo(pageForm);
			}

			const is_close_save = $btn.is('#save, #popupsave') || String($btn.data('save-action') ?? '') === 'close';

			$hidden.val(is_close_save ? '1' : '0');
		};

		const is_content_managed_save = () => {
			const $save_stay = $('#save-stay, #popupsave-stay').first();

			return $save_stay.length > 0 && $save_stay.attr('type') === 'button';
		};

		pageForm.on('click', '#save, #save-stay, #popupsave, #popupsave-stay', async (e) => {
			const $btn = $(e.currentTarget);
			syncCloseAfterSaveHiddenField($btn);
			// Distinguish real user click from programmatic click (e.g. "Salva ed esci" modal)
			const is_user_click = (e.originalEvent ?? e).isTrusted === true;
			// Se il pulsante ha già la classe 'submitting', blocca il click
			if ($btn.hasClass('submitting')) {
				e.preventDefault();
				return false;
			}

			if (this.dispatchChangeTimeout) {
				clearTimeout(this.dispatchChangeTimeout);
				this.dispatchChangeTimeout = null;
			}

			this.clearConfirmPageLeave();

			if (is_user_click && window.originalFormAction && window.originalFormAction !== pageForm.attr('action')) {
				pageForm.attr('action', window.originalFormAction);
				window.originalFormAction = null;
			}

			const backButton = $('#close');
			const is_close_save = $btn.is('#save, #popupsave') || String($btn.data('save-action') ?? '') === 'close';
			// Sync forms: skip POST when there are no edits (user clicked Salva e chiudi on a clean form).
			if (!is_content_managed_save() && is_user_click && is_close_save && backButton.length && !this.shouldAttemptSave()) {
				this.notifyNoChangesToSave();
				if (backButton.attr('href')) {
					window.location.href = backButton.attr('href');
				} else {
					backButton.trigger('click');
				}
				e.preventDefault();
				return false;
			}

			if ($btn.attr('type') === 'submit') {
				e.preventDefault();
				e.stopImmediatePropagation();

				const is_valid = await this.validateForSubmit();

				if (!is_valid) {
					this.resetSaveButtonLoadingState();

					return false;
				}

				if (!is_close_save && !this.shouldAttemptSave()) {
					this.notifyNoChangesToSave();
					this.resetSaveButtonLoadingState();

					return false;
				}

				this.suppressLeaveConfirm = true;
				$btn.addClass('submitting');
				$btn.css({
					cursor: 'not-allowed',
					opacity: '0.75',
				});
				window.naxosTranslationToolbarBridge?.schedulePublishState?.(true);
				FormUtils.prepareReadonlyFieldsForSubmit(pageForm);

				// formValidation hijacks the form's `submit` event: it always preventDefault()s and only
				// lets the POST through when its internal `_submitIfValid` flag is set, which happens
				// exclusively on a genuine click of a submit button it owns. requestSubmit() fires `submit`
				// without that click, so on non-async (type=submit) forms — folders, sections, tags, … —
				// the POST would silently never happen and the Save button would spin forever. We already
				// validated above, so drop formValidation's submit guard before submitting natively.
				const form_validator = pageForm.data('formValidation');

				if (form_validator) {
					pageForm.off('submit.' + (form_validator._namespace || 'fv'));
				}

				pageForm[0].requestSubmit($btn[0]);
				this.scheduleSyncSubmitSafetyReset($btn);

				return false;
			}

			// Async content save (type=button): contents.js owns click handling and validation.
			if (is_content_managed_save()) {
				return;
			}

			$btn.addClass('submitting');
			$btn.css({
				cursor: 'not-allowed',
				opacity: '0.75',
			});
			window.naxosTranslationToolbarBridge?.schedulePublishState?.(true);
		});

		// Form-editing mode: mirror the checkbox (do not blind-toggle). Switchery copies id/name
		// onto the span, so read the real input; sync on init for ?editing=1 and after each click.
		const syncFormEditingMode = () => {
			const switch_checked = Boolean($('input[name="form-editing-mode"]').prop('checked'));
			pageForm.toggleClass('editing-mode', resolveFormEditingModeEnabled({ switchChecked: switch_checked }));
		};

		syncFormEditingMode();
		$('.form-editing-mode-container .switchery').on('click', syncFormEditingMode);
		$('input[name="form-editing-mode"]').on('change', syncFormEditingMode);
	}

	validateFormFields() {
		const required_fields = $('#pageForm :input[required]').filter((_idx, element) => Form.isFieldVisibleForValidation(element));
		let all_fields_valid = true;

		required_fields.each((_idx, element) => {
			if (typeof element.checkValidity === 'function' && !element.checkValidity()) {
				all_fields_valid = false;
				return false;
			}
		});

		return all_fields_valid;
	}

	/**
	 * Marks visible empty/invalid required fields red (is-invalid) and clears the marker from the ones now valid.
	 *
	 * Restores the feedback users got pre-refactor when clicking Save: required fields the user never
	 * touched (e.g. Firme, Sezioni) would otherwise never turn red until submit validation runs.
	 */
	revealRequiredFieldErrors() {
		if (!$('#pageForm').length || window.forceReadonly) {
			return;
		}

		$('#pageForm :input[required]').each((_idx, element) => {
			const $field = $(element);
			const invalid = Form.isFieldVisibleForValidation(element) && typeof element.checkValidity === 'function' && !element.checkValidity();

			const $target = $field.hasClass('select2-hidden-accessible') ? $field.siblings('.select2-container').find('.select2-selection') : $field;

			$target.toggleClass('is-invalid', invalid);
			FormUtils.syncInvalidFieldTooltip($field, invalid);
		});
	}

	/**
	 * Updates leave guard and inline required-field markers after edits.
	 * Save buttons stay enabled; use validateForSubmit() on click/submit instead.
	 */
	refreshSaveButtonState() {
		if (this._refresh_save_button_state_running) {
			return;
		}

		if (!this._user_interacted && Date.now() < this._init_settle_deadline) {
			this.scheduleFinalizeInitialFormState(300);

			return;
		}

		this._refresh_save_button_state_running = true;

		try {
			const $page_form = $('#pageForm');

			if (!$page_form.length || window.forceReadonly) {
				this.setSaveButtonsEnabled(false);

				return;
			}

			const dirty = this.isDirty();

			if (dirty && !this.needsConfirm) {
				this.setConfirmPageLeave();
			} else if (!dirty && this.needsConfirm) {
				// The form was edited (leave guard armed) but has since been reverted to its original
				// values — e.g. a switch toggled and toggled back. Without clearing the guard the
				// browser keeps warning "Changes you made may not be saved" on reload/navigation even
				// though there is nothing to save.
				this.clearConfirmPageLeave();
			}

			this.setSaveButtonsEnabled(true);

			if (dirty) {
				this.revealRequiredFieldErrors();
			}
		} finally {
			this._refresh_save_button_state_running = false;
		}
	}

	/**
	 * Lists the tracked fields whose value differs from the last captured snapshot. Used both for
	 * dirty detection and for the debug log so we can see exactly which field flipped the form to
	 * dirty (and weed out changes that are applied automatically rather than by the user).
	 *
	 * @returns {Array<{ id: string, initial: *, current: * }>}
	 */
	getDirtyFields() {
		if (!$('#pageForm').length || window.forceReadonly || this._initial_baseline_pending) {
			return [];
		}

		const dirty_fields = [];

		$('#pageForm')
			.find(Form.getBaselineFieldSelector())
			.each((_idx, element) => {
				const id = $(element).attr('id');

				if (!id) {
					return;
				}

				const current_value = this.readTrackedFieldValue(element);
				const initial_value = this.initialFormData[id];

				if (current_value !== initial_value) {
					dirty_fields.push({ id, initial: initial_value, current: current_value });
				}
			});

		return dirty_fields;
	}

	/**
	 * True when main form field values differ from the last captured snapshot (same scope as formChangeHandler).
	 */
	isDirty() {
		return this.getDirtyFields().length > 0;
	}

	async formChangeHandler(evt, forced = false) {
		if (this.suppressLeaveConfirm) {
			return;
		}

		// A real user event (native change/input, forced summernote edit) counts as interaction and
		// freezes the baseline. Programmatic widget-init changes have no trusted originalEvent.
		if (forced || evt?.originalEvent?.isTrusted) {
			this._user_interacted = true;
		}

		if (this._initial_baseline_pending) {
			// User already edited while widgets were still hydrating: close the pending window
			// without absorbing the edit into the clean baseline (REDMINE-2209).
			if (this._user_interacted) {
				this.finalizeInitialFormState();
			}

			return;
		}

		// During the settle window, programmatic widget-init changes re-baseline instead of marking
		// dirty. Genuine user edits must NOT be absorbed: an edit made right after opening would
		// otherwise be folded into the baseline and never seen as unsaved.
		if (!this._user_interacted && Date.now() < this._init_settle_deadline) {
			this.scheduleFinalizeInitialFormState(300);

			return;
		}

		// Check if any field has changed
		let form_changed = forced;
		const $page_form = $('#pageForm');

		if (!forced && !this.needsConfirm) {
			$page_form.find(Form.getBaselineFieldSelector()).each((_idx, element) => {
				const id = $(element).attr('id');

				if (!id) {
					return;
				}

				const current_value = this.readTrackedFieldValue(element);
				const initial_value = this.initialFormData[id];

				if (current_value !== initial_value) {
					form_changed = true;
					return false;
				}
			});
		} else if (this.needsConfirm) {
			form_changed = true;
		}

		if (form_changed) {
			await this.refreshSaveButtonState();
		}
	}
}
